<script lang="ts">
import { defineComponent } from 'vue';
import { formatCurrency, formatMoney } from '@/utils/format';
import Decimal from 'decimal.js-light';

export default defineComponent({
  name: 'row-actual-multiple-outcome',
  computed: {
    odds() {
      return this.stake.bets.map((bet: { odds: string }) => bet.odds);
    },
    totalResultAmount() {
      return this.totalResultRow.length
        ? this.totalResultRow.reduce(
            (acc, cur) => acc.mul(cur.odds),
            new Decimal(this.stake.stakeAmount)
          )
        : null;
    },
    totalResultVendorAmount() {
      return this.totalResultRow.length
        ? this.totalResultRow.reduce(
            (acc, cur) => acc.mul(cur.odds),
            new Decimal(this.stake.vendorStakeAmount)
          )
        : null;
    },
    totalResultRow() {
      const row: { value: number | string; css: string; odds: Decimal }[] = [];

      for (const bet of this.stake.bets) {
        let odds = new Decimal(bet.odds);

        switch (bet.selection.ResultStatus) {
          case 'Winner':
            row.push({ value: bet.odds, css: 'bg-green-300 dark:bg-green-700', odds });
            break;

          case 'Loser':
            odds = new Decimal(0);
            row.push({ value: bet.odds, css: 'bg-red-300 dark:bg-red-700', odds });
            break;

          case 'Voided':
            odds = new Decimal(1);
            row.push({ value: 1, css: 'bg-yellow-300 dark:bg-yellow-700', odds });
            break;

          case 'Placed':
            odds = odds.div(bet.selection.AdditionalResultData.countInPlace);
            row.push({ value: odds.toNumber(), css: 'bg-yellow-300 dark:bg-yellow-700', odds });
            break;

          case 'Partial':
            const winPart = new Decimal(bet.selection.AdditionalResultData.PercentageWin)
              .div(100)
              .mul(odds);
            const pushPart = new Decimal(bet.selection.AdditionalResultData.PercentagePush).div(
              100
            );

            odds = winPart.plus(pushPart);

            row.push({
              value: `${winPart.toNumber()} + ${pushPart.toNumber()}`,
              css: 'bg-yellow-300 dark:bg-yellow-700',
              odds,
            });

            break;
        }
      }

      return row;
    },

    stakeRow() {
      const row: { value: number | string; css: string }[] = [];

      for (const bet of this.stake.bets) {
        switch (bet.selection.ResultStatus) {
          case 'Winner':
            row.push({ value: bet.odds, css: 'bg-green-300 dark:bg-green-700' });
            break;
          // TODO fix Placed to calculate partial win
          case 'Placed':
          case 'Loser':
            row.push({ value: bet.odds, css: 'bg-red-300 dark:bg-red-700' });
            break;
          case 'Pushed':
            row.push({ value: 1, css: 'bg-yellow-300 dark:bg-yellow-700' });
            break;
          case 'Partial':
            row.push({
              value: Math.floor(bet.odds * bet.selection.AdditionalResultData.PercentageWin) / 100,
              css: 'bg-yellow-300 dark:bg-yellow-700',
            });
            break;
          default:
            row.push({ value: bet.odds, css: 'bg-gray-300 dark:bg-gray-700' });
            break;
        }
      }

      return row;
    },
  },
  methods: { formatMoney, formatCurrency },
  props: ['stake'],
});
</script>

<template>
  <span>Накопленный выигрыш</span>
  <span class="col-span-2">
    <span class="flex gap-1 text-center justify-start vertical-mid flex-wrap items-center">
      <template v-for="k in totalResultRow">
        <span :class="{ 'px-2 rounded': true, [k.css]: true }">
          {{ k.value }}
        </span>
        <span class="px-2">&#x00D7;</span>
      </template>
      <span class="bg-green-300 dark:bg-green-700 px-2 rounded">
        <b>{{ formatMoney(+stake.stakeAmount) }}</b>
        {{ formatCurrency(+stake.stakeAmount, stake.currency) }}
      </span>
      <span class="text-gray-500 text-lg mx-2"> = </span>
      <template v-if="totalResultAmount">
        <span class="text-lg">
          {{ formatMoney(totalResultAmount.toNumber()) }}
          {{ formatCurrency(totalResultAmount.toNumber(), stake.currency) }}
        </span>
        <span class="text-lg text-gray-500 ml-2">
          {{ formatMoney(totalResultVendorAmount!.toNumber(), 2, false) }}
          {{ formatCurrency(totalResultVendorAmount!.toNumber(), stake.vendorCurrency) }}
        </span>
      </template>
      <span class="text-lg" v-else> ... </span>
    </span>
  </span>
</template>

<style scoped lang="scss"></style>
